// ─────────────────────────────────────────────────────────────────────────────
// cvService.ts — API service layer for CV data
//
// This is the only place that knows about HTTP / API calls.
// Components and Redux thunks call this service — they never use axios/fetch
// directly.  The base URL is resolved from the Redux store's active back-end
// selection, making it trivial to hot-swap the architecture.
// ─────────────────────────────────────────────────────────────────────────────

import axios from 'axios';
import type { CVData } from '@/types/cv.types';
import { store } from '@/store';
import { BACKEND_PORTS, BACKEND_HOSTS } from '@/store/uiSlice';
import { getDevHost } from '@/utils/devHost';

// ── Axios instance factory ─────────────────────────────────────────────────────
// Called on each request so it always picks up the latest backend selection
// from Redux (avoids stale closures from module-level instance creation).
function createApiClient() {
  const architecture = store.getState().ui.backEndArchitecture;
  const baseURL       = import.meta.env.DEV
    ? `http://${getDevHost()}:${BACKEND_PORTS[architecture]}/api`  // Dev: target specific port, on whatever host served this page (localhost or a Tailscale IP)
    : `https://${BACKEND_HOSTS[architecture]}/api`;                // Prod: target the api-* subdomain

  return axios.create({
    baseURL,
    timeout: 10_000, // 10 seconds
    headers: { 'Content-Type': 'application/json' },
  });
}

// ── CV Service ────────────────────────────────────────────────────────────────
export const cvService = {
  /**
   * getCV — Fetches the full CV data object from the active back-end.
   *
   * Falls back to the local shared/cv-data.json if the API returns an error
   * (e.g. when no backend is running in development).  This keeps the CV
   * page functional even without a running server.
   */
  async getCV(): Promise<CVData> {
    try {
      const client = createApiClient();
      const { data } = await client.get<CVData>('/cv');
      return data;
    } catch {
      // Graceful fallback to the bundled JSON asset
      console.warn('[cvService] API unavailable — loading local cv-data.json');
      const fallback = await fetch('/cv-data.json');
      if (!fallback.ok) throw new Error('Failed to load fallback CV data');
      return fallback.json() as Promise<CVData>;
    }
  },

  /**
   * healthCheck — Pings the active backend to confirm it is reachable.
   * Returns true if the server responds with 200 within the timeout.
   */
  async healthCheck(): Promise<boolean> {
    try {
      const client = createApiClient();
      await client.get('/health');
      return true;
    } catch {
      return false;
    }
  },
};
